import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, getDoc, doc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useOrgId } from '../../context/OrgContext';
import { useOrgData } from '../../hooks/useOrgData';
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [2023, 2024, 2025, 2026, 2027, 2028];
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const fmt = (n) => n > 0
  ? `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  : '—';

const monthAbbrs = MONTHS;

const BudgetByMonth = () => {
  const orgId = useOrgId();
  const { transactionTypes } = useOrgData(orgId);

  const [transactions, setTransactions] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(CURRENT_YEAR);

  useEffect(() => {
    setLoading(true);
    const q = query(collection(db, 'orgs', orgId, 'transactions'), where('year', '==', year));
    const unsub = onSnapshot(q, snap => {
      setTransactions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [orgId, year]);

  useEffect(() => {
    getDoc(doc(db, 'orgs', orgId, 'budgets', String(year))).then(snap => {
      setBudgets(snap.exists() ? (snap.data().lines || []) : []);
    });
  }, [orgId, year]);

  const activeTx = transactions.filter(t => t.type !== 'Starting Balance');

  const getMonthlyActuals = (typeName, fundName, kind) =>
    MONTHS.map((_, i) => {
      const mStr = String(i + 1).padStart(2, '0');
      return activeTx.reduce((sum, t) => {
        const txM = (t.stmtMonth || t.date || '')?.split('-')[1];
        if (txM !== mStr) return sum;
        if (t.split && t.lines?.length) {
          const inKind = kind === 'income' ? t.income > 0 : t.expense > 0;
          if (!inKind) return sum;
          return sum + t.lines
            .filter(l => l.type === typeName && (l.classification || '') === fundName)
            .reduce((s, l) => s + (l.amount || 0), 0);
        }
        if (t.type === typeName && (t.classification || '') === fundName) {
          return sum + (kind === 'income' ? (t.income || 0) : (t.expense || 0));
        }
        return sum;
      }, 0);
    });

  // Build display rows: budget lines + any transaction types with actuals not in budget
  const budgetedKeys = new Set(budgets.map(l => `${l.fund}::${l.type}`));
  const extraFromTx = [];
  activeTx.forEach(tx => {
    const key = `${tx.classification}::${tx.type}`;
    if (!budgetedKeys.has(key)) {
      budgetedKeys.add(key);
      const t = transactionTypes.find(t => t.name === tx.type);
      if (t) extraFromTx.push({ fund: tx.classification || '', type: tx.type, monthly: 0, kind: t.kind });
    }
  });

  const allLines = [
    ...budgets.map(l => {
      const t = transactionTypes.find(t => t.name === l.type);
      return { ...l, kind: t?.kind || 'expense' };
    }),
    ...extraFromTx,
  ];

  const bills    = allLines.filter(l => l.kind === 'expense');
  const receipts = allLines.filter(l => l.kind === 'income');

  const Section = ({ label, lines, kindColor }) => {
    if (lines.length === 0) return null;
    // Group by fund
    const byFund = {};
    lines.forEach(l => {
      const f = l.fund || 'Unassigned';
      if (!byFund[f]) byFund[f] = [];
      byFund[f].push(l);
    });
    return (
      <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6">
        <div className="px-4 py-3 bg-slate-100 border-b border-slate-200">
          <h3 className="font-semibold text-slate-700">{label}</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 text-slate-600 border-b border-gray-200">
                <th className="px-3 py-2.5 text-left font-semibold min-w-[120px]">Fund</th>
                <th className="px-3 py-2.5 text-left font-semibold min-w-[140px]">Category</th>
                <th className="px-3 py-2.5 text-right font-semibold whitespace-nowrap">Monthly Budget</th>
                {monthAbbrs.map(m => (
                  <th key={m} className="px-2 py-2.5 text-right font-semibold">{m}</th>
                ))}
                <th className="px-3 py-2.5 text-right font-semibold">YTD Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {Object.entries(byFund).map(([fund, flines]) => flines.map((l, i) => {
                const monthly = getMonthlyActuals(l.type, l.fund || '', l.kind);
                const ytd = monthly.reduce((s, v) => s + v, 0);
                return (
                  <tr key={`${fund}-${l.type}-${i}`} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-slate-500">{i === 0 ? fund : ''}</td>
                    <td className="px-3 py-2 font-medium text-slate-700">{l.type}</td>
                    <td className="px-3 py-2 text-right text-gray-500">
                      {l.monthly > 0 ? fmt(l.monthly) : 'N/A'}
                    </td>
                    {monthly.map((v, mi) => (
                      <td key={mi} className={`px-2 py-2 text-right ${v === 0 ? 'text-gray-300' : kindColor}`}>
                        {fmt(v)}
                      </td>
                    ))}
                    <td className={`px-3 py-2 text-right font-bold ${ytd === 0 ? 'text-gray-300' : kindColor}`}>
                      {fmt(ytd)}
                    </td>
                  </tr>
                );
              }))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Budget by Month</h2>
          <p className="text-slate-500 text-sm mt-0.5">Actual vs budget, month by month</p>
        </div>
        <div className="flex gap-2 no-print">
          <select
            value={year} onChange={e => setYear(Number(e.target.value))}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white"
          >
            {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button
            onClick={() => window.print()}
            className="border border-gray-300 px-3 py-1.5 rounded-lg text-sm hover:bg-gray-50 transition-colors"
          >
            Print
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-500">Loading…</div>
      ) : allLines.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          No budget or transactions for {year}. Set budgets in Admin → Budget.
        </div>
      ) : (
        <>
          <Section label="Money Out (Withdrawals)" lines={bills} kindColor="text-red-500" />
          <Section label="Money In (Deposits)" lines={receipts} kindColor="text-green-600" />
        </>
      )}
    </div>
  );
};

export default BudgetByMonth;
